import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { Field, Select } from '@/ui/Field';
import { Skeleton, SkeletonFilas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import { useAreasDeGestion } from '../api';
import {
  FILTROS_VACIOS, useInduccion, useRecordarATodos,
  type EstadoDeInduccion, type FiltrosDeInduccion, type Induccion as DatosDeInduccion,
} from './api';
import { ESTADO, LEYENDA, TablaDeInduccion } from './TablaDeInduccion';

/**
 * Inducción — `#induccion`. Módulo 11, opción 5, la última.
 *
 * Quién terminó los cursos obligatorios y a quién le faltan, con un
 * recordatorio por correo para una persona o para todas las de la lista. Solo
 * gestión: `soloGestion` en el PAGE_MAP y `require_role("coordinador", "admin")`
 * en el servidor.
 *
 * ## Lo que cambia
 *
 * En el servidor (`induccion_service.py` y `admin.py`): solo cuentan los cursos
 * obligatorios publicados, el avance se promedia sobre todos, el recordatorio
 * respeta el área de quien lo envía y los filtros de la tabla, y el correo sale
 * con la configuración SMTP del panel.
 *
 * En la pantalla:
 * - **La fecha de ingreso salía un día antes**: `fmtDate` lee un `yyyy-mm-dd`
 *   como medianoche UTC, que en Bogotá es la tarde del día anterior.
 * - Las tarjetas del resumen eran `<div onclick>`: sin teclado y sin decir cuál
 *   estaba puesta. Ahora son botones con `aria-pressed`.
 * - El filtro de rol tenía tres escritos a mano; en la base hay cuatro.
 * - A un gestor acotado a su área se le ofrecían todas, y el servidor ignoraba
 *   la elegida sin decirlo.
 * - Las áreas se pedían con un `catch` que devolvía `[]`: si fallaban, el
 *   filtro decía «Todas las áreas» y no había otra.
 * - Un fallo al cargar era un estado vacío con el mensaje, sin reintentar.
 * - «Sin usuarios en este estado» salía también cuando lo que no encontraba
 *   nada era la búsqueda.
 * - «Recordar a todos» decía «todos» con un filtro puesto; ahora dice a
 *   cuántas personas, y son las de la lista.
 */

const TARJETAS: ReadonlyArray<{
  valor: EstadoDeInduccion | '';
  texto: string;
  icono: string;
  tinte: string;
  tinta: string;
}> = [
  { valor: '', texto: 'Personas', icono: 'bi-people-fill', tinte: 'bg-sunken', tinta: 'text-brand-ink' },
  { valor: 'al_dia', texto: ESTADO.al_dia.texto, icono: ESTADO.al_dia.icono, tinte: 'bg-ok/12', tinta: 'text-ok-ink' },
  { valor: 'pendiente', texto: ESTADO.pendiente.texto, icono: ESTADO.pendiente.icono, tinte: 'bg-warn/16', tinta: 'text-warn-ink' },
  { valor: 'sin_iniciar', texto: ESTADO.sin_iniciar.texto, icono: ESTADO.sin_iniciar.icono, tinte: 'bg-danger/12', tinta: 'text-danger-ink' },
];

const personas = (n: number) => (n === 1 ? '1 persona' : `${n} personas`);

export function Induccion() {
  const [filtros, setFiltros] = useState<FiltrosDeInduccion>(FILTROS_VACIOS);
  // Lo escrito se separa de lo que viaja: sin esto, cada tecla recalculaba la
  // inducción de toda la empresa. 350 ms, lo que ya usaba el vanilla.
  const [texto, setTexto] = useState('');
  const [confirmando, setConfirmando] = useState(false);
  // A quién se le recordó en esta visita: su botón pasa a «Enviado» para no
  // mandarlo dos veces sin querer. No es un registro —ese queda en la
  // bitácora— y se olvida al salir.
  const [recordados, setRecordados] = useState<ReadonlySet<number>>(() => new Set());

  useEffect(() => {
    const t = window.setTimeout(() => {
      setFiltros((f) => (f.search === texto.trim() ? f : { ...f, search: texto.trim() }));
    }, 350);
    return () => window.clearTimeout(t);
  }, [texto]);

  const consulta = useInduccion(filtros);
  const areas = useAreasDeGestion(true);
  const masivo = useRecordarATodos();

  const d = consulta.data;
  const actualizando = consulta.isPlaceholderData;
  const hayFiltros = !!(filtros.status || filtros.role || filtros.area_id || filtros.search);
  const pendientes = d ? d.items.filter((p) => p.status !== 'al_dia') : [];

  const cambiar = (parcial: Partial<FiltrosDeInduccion>) => setFiltros((f) => ({ ...f, ...parcial }));
  const limpiar = () => { setTexto(''); setFiltros(FILTROS_VACIOS); };
  const marcar = (ids: number[]) => setRecordados((s) => new Set([...s, ...ids]));

  function recordarATodos() {
    // Los ids se toman ANTES de enviar: son los de la lista que se confirmó.
    const ids = pendientes.map((p) => p.user_id);
    masivo.mutate(filtros, {
      onSuccess: (r) => {
        const enviados = Number(r.sent) || 0;
        Utils.toast(r.message, enviados ? 'success' : 'info');
        if (enviados) marcar(ids);
        setConfirmando(false);
      },
      onError: (err) =>
        Utils.toast(err instanceof Error ? err.message : 'No se pudieron enviar los recordatorios', 'error'),
    });
  }

  return (
    <>
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-6 font-bold text-brand-ink">
            <i className="bi bi-clipboard2-check" aria-hidden="true" /> Inducción del personal
          </h1>
          <p className="mt-1 text-3 text-muted">
            Quién terminó los cursos obligatorios y a quién le faltan.
          </p>
        </div>
        {d && d.total_mandatory > 0 && (
          <Button
            onClick={() => setConfirmando(true)}
            disabled={!pendientes.length || actualizando}
            razonDeshabilitado={
              actualizando ? 'Actualizando la lista…' : 'Nadie de la lista tiene la inducción pendiente'
            }
          >
            <i className="bi bi-envelope-paper" aria-hidden="true" />
            {pendientes.length ? `Recordar a ${personas(pendientes.length)}` : 'Recordar a las pendientes'}
          </Button>
        )}
      </header>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="search-bar min-w-60 flex-1">
          <i className="bi bi-search search-bar-icon" aria-hidden="true" />
          <input
            type="search"
            className="search-input"
            aria-label="Buscar por nombre o correo"
            placeholder="Buscar por nombre o correo…"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
        </div>
        <Field label="Rol" className="min-w-[11rem]">
          <Select value={filtros.role} onChange={(e) => cambiar({ role: e.target.value })} disabled={!d}>
            <option value="">Todos los roles</option>
            {(d?.roles ?? []).map((r) => (
              <option key={r.key} value={r.key}>{r.name}</option>
            ))}
          </Select>
        </Field>
        <FiltroDeArea
          datos={d}
          areas={areas.data}
          cargandoAreas={areas.isPending}
          valor={filtros.area_id}
          onCambio={(area_id) => cambiar({ area_id })}
        />
        {hayFiltros && (
          <Button variant="ghost" onClick={limpiar}>
            <i className="bi bi-x-circle" aria-hidden="true" /> Quitar filtros
          </Button>
        )}
      </div>

      {areas.isError && !d?.scoped_area && (
        <p className="-mt-2 mb-4 flex flex-wrap items-center gap-2 text-2 text-danger-ink">
          <i className="bi bi-exclamation-triangle" aria-hidden="true" />
          No se pudieron leer las áreas, así que no se puede filtrar por una.
          <Button variant="ghost" size="sm" onClick={() => void areas.refetch()}>Reintentar</Button>
        </p>
      )}

      {consulta.isPending ? (
        <Esqueleto />
      ) : consulta.isError ? (
        <EstadoError err={consulta.error} que="la inducción" onReintentar={() => void consulta.refetch()} />
      ) : !d ? null : d.total_mandatory === 0 ? (
        <EstadoVacio
          icono="bi-mortarboard"
          titulo="No hay cursos obligatorios"
          descripcion="Marca un curso publicado como obligatorio y aquí saldrá quién lo ha terminado. Un borrador no cuenta: todavía no lo ve nadie."
          accion={{ texto: 'Ir al catálogo', onClick: () => { location.hash = '#cursos'; } }}
        />
      ) : (
        <>
          <Resumen
            resumen={d.summary}
            activo={filtros.status}
            onElegir={(status) => cambiar({ status })}
          />
          <CursosObligatorios cursos={d.mandatory_courses} />
          {d.items.length ? (
            <TablaDeInduccion
              personas={d.items}
              roles={d.roles}
              actualizando={actualizando}
              recordados={recordados}
              onRecordado={(id) => marcar([id])}
            />
          ) : (
            <SinResultados d={d} filtros={filtros} onLimpiar={limpiar} />
          )}
        </>
      )}

      <ConfirmDialog
        abierto={confirmando}
        onCambio={(v) => { if (!masivo.isPending) setConfirmando(v); }}
        titulo="Recordar la inducción"
        descripcion={
          `Se enviará un correo a ${personas(pendientes.length)}${hayFiltros ? ' de la lista' : ''} ` +
          'con cursos obligatorios pendientes. Cada una recibe los que le faltan.'
        }
        textoConfirmar={
          pendientes.length === 1 ? 'Enviar 1 recordatorio' : `Enviar ${pendientes.length} recordatorios`
        }
        cargando={masivo.isPending}
        onConfirmar={recordarATodos}
      />
    </>
  );
}

/** El área, o la del gestor acotado. A quien solo ve la suya no se le ofrecen
 *  las demás: el servidor la fija pida la que pida. */
function FiltroDeArea({
  datos, areas, cargandoAreas, valor, onCambio,
}: {
  datos: DatosDeInduccion | undefined;
  areas: Array<{ id: number; name: string }> | undefined;
  cargandoAreas: boolean;
  valor: string;
  onCambio: (v: string) => void;
}) {
  if (datos?.scoped_area) {
    return (
      <p className="flex min-h-10 items-center gap-1.5 rounded-sm border border-line bg-sunken px-3 text-2 text-ink-2">
        <i className="bi bi-geo-alt" aria-hidden="true" />
        Tu área: <strong className="text-ink">{datos.scoped_area.name ?? '—'}</strong>
      </p>
    );
  }
  return (
    <Field label="Área" className="min-w-[12rem]">
      <Select value={valor} onChange={(e) => onCambio(e.target.value)} disabled={!datos || !areas}>
        <option value="">{cargandoAreas ? 'Cargando áreas…' : 'Todas las áreas'}</option>
        {(areas ?? []).map((a) => (
          <option key={a.id} value={String(a.id)}>{a.name}</option>
        ))}
      </Select>
    </Field>
  );
}

/** Las tarjetas del resumen, que filtran por estado. Se cuentan antes del
 *  filtro, así que no cambian al pulsarlas; pulsar la puesta la quita. */
function Resumen({
  resumen, activo, onElegir,
}: {
  resumen: DatosDeInduccion['summary'];
  activo: EstadoDeInduccion | '';
  onElegir: (v: EstadoDeInduccion | '') => void;
}) {
  return (
    <div role="group" aria-label="Filtrar por estado" className="mb-4 grid grid-cols-2 gap-2 lg:grid-cols-4">
      {TARJETAS.map((t) => {
        const puesta = activo === t.valor;
        const n = t.valor ? resumen[t.valor] : resumen.total;
        return (
          <button
            key={t.valor || 'todas'}
            type="button"
            aria-pressed={puesta}
            onClick={() => onElegir(puesta ? '' : t.valor)}
            className={cn(
              'flex min-w-0 items-center gap-3 rounded-md border bg-surface p-3 text-left',
              'focus-visible:focus-anillo',
              puesta
                ? 'border-brand-ink ring-1 ring-brand-ink'
                : 'border-line hover:border-line-strong',
            )}
          >
            <span
              className={cn('grid size-10 shrink-0 place-items-center rounded-full text-4', t.tinte, t.tinta)}
              aria-hidden="true"
            >
              <i className={cn('bi', t.icono)} />
            </span>
            <span className="min-w-0">
              <span className={cn('block text-6 font-extrabold leading-none tabular-nums', t.valor ? t.tinta : 'text-ink')}>
                {n}
              </span>
              <span className="mt-1 block truncate text-2 text-muted">{t.texto}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Los cursos que cuentan, cada uno con su enlace: es desde su ficha desde
 *  donde se asigna a quien le falta. */
function CursosObligatorios({ cursos }: { cursos: Array<{ id: number; title: string }> }) {
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-x-6 gap-y-2 text-2 text-muted">
      <p className="flex flex-wrap items-center gap-1.5">
        <i className="bi bi-mortarboard" aria-hidden="true" />
        {cursos.length === 1 ? '1 curso obligatorio:' : `${cursos.length} cursos obligatorios:`}
        {cursos.map((c) => (
          <a
            key={c.id}
            href={`#course-detail/${c.id}`}
            className="rounded-sm bg-sunken px-2 py-0.5 text-ink-2 hover:text-brand-ink focus-visible:focus-anillo"
          >
            {c.title}
          </a>
        ))}
      </p>
      {/* Solo para la vista: cada curso de la tabla dice su estado en texto. */}
      <ul className="flex flex-wrap gap-x-3 gap-y-1 text-1" aria-hidden="true">
        {LEYENDA.map((l) => (
          <li key={l.texto} className="flex items-center gap-1">
            <i className={cn('bi', l.icono, l.tinta)} /> {l.texto}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Nadie en la lista. El vanilla decía «Sin usuarios en este estado» aunque lo
 *  que no encontrara nada fuera la búsqueda. */
function SinResultados({
  d, filtros, onLimpiar,
}: {
  d: DatosDeInduccion;
  filtros: FiltrosDeInduccion;
  onLimpiar: () => void;
}) {
  const soloEstado = filtros.status && !filtros.role && !filtros.area_id && !filtros.search;
  if (soloEstado && d.summary.total > 0) {
    return (
      <EstadoVacio
        compacto
        icono="bi-funnel"
        titulo={`Nadie está «${ESTADO[filtros.status as EstadoDeInduccion].texto}»`}
        descripcion={`Hay ${personas(d.summary.total)} en la lista; ninguna en este estado.`}
        accion={{ texto: 'Ver a todas', onClick: onLimpiar }}
      />
    );
  }
  const hayFiltros = filtros.status || filtros.role || filtros.area_id || filtros.search;
  return hayFiltros ? (
    <EstadoVacio
      compacto
      icono="bi-funnel"
      titulo="Nadie coincide con los filtros"
      descripcion="Prueba con otra búsqueda o quita algún filtro."
      accion={{ texto: 'Quitar filtros', onClick: onLimpiar }}
    />
  ) : (
    <EstadoVacio compacto icono="bi-people" titulo="No hay personas activas" />
  );
}

function Esqueleto() {
  return (
    <div aria-busy="true" aria-label="Cargando la inducción">
      <div className="mb-4 grid grid-cols-2 gap-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16" />)}
      </div>
      <Skeleton className="mb-3 h-5 w-72" />
      <SkeletonFilas filas={5} columnas={5} />
    </div>
  );
}
